import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Article as ArticleIcon, AutoFixHigh as AutoFixHighIcon, FactCheck as FactCheckIcon, Language as LanguageIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import axios from 'axios';
import { updateCompany } from '../../api/companies';
import { CompanyUpdate } from '../../types/company';
import { useAuth } from '../../hooks/useAuth';
import WebsiteCell from './WebsiteCell';
import BatchEnhancementPanel from './BatchEnhancementPanel';
import BatchBackfillPanel from './BatchBackfillPanel';
import BackfillActivityPanel from './BackfillActivityPanel';
import BatchQuartrCheckPanel from './BatchQuartrCheckPanel';
import BatchUrlDiscoveryPanel from './BatchUrlDiscoveryPanel';
import { isInactiveCorporateStatus } from '../../constants/corporateStatus';
import {
  COVERAGE_PRIORITY_OPTIONS,
  formatCoveragePriority,
} from '../../constants/coveragePriority';
import {
  getContentCompletionOverview,
  getSozoChannels,
  getSozoServers,
} from '../../api/contentCompletion';
import {
  ChannelCompletionSummary,
  CompanyContentHealth,
  ContentCompletionSortField,
  ContentHealthStatus,
  CorporateStatusFilter,
  CoveragePriorityFilter,
  QuartrHealthStatus,
  SozoChannelInfo,
  SozoServerInfo,
} from '../../types/contentCompletion';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import CompanyDetailDrawer from './CompanyDetailDrawer';
import FeedIssuesPanel from './FeedIssuesPanel';
import CompaniesToReviewPanel from './CompaniesToReviewPanel';
import QuartrIssuesPanel from './QuartrIssuesPanel';
import {
  PR_STATUS_LABELS,
  QUARTR_STATUS_LABELS,
  formatLastContentDate,
  prStatusTooltip,
  quartrStatusTooltip,
} from './contentHealthLabels';

const STATUS_LABELS = PR_STATUS_LABELS;

const STATUS_COLORS: Record<
  ContentHealthStatus,
  'success' | 'warning' | 'error' | 'default'
> = {
  active: 'success',
  stale: 'warning',
  no_content: 'error',
  no_feed: 'default',
};

const QUARTR_STATUS_COLORS: Record<
  QuartrHealthStatus,
  'success' | 'warning' | 'error' | 'default' | 'info'
> = {
  active: 'success',
  stale: 'warning',
  no_content: 'error',
  no_feed: 'default',
  missing_id: 'info',
};

const formatCorporateStatus = (value?: string) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const emptyStatusCounts = () => ({
  active: 0,
  stale: 0,
  no_content: 0,
  no_feed: 0,
});

const emptyQuartrStatusCounts = (): Record<QuartrHealthStatus, number> => ({
  active: 0,
  stale: 0,
  no_content: 0,
  no_feed: 0,
  missing_id: 0,
});

const STALE_DAYS_MIN = 1;
const STALE_DAYS_MAX = 365;
const STALE_DAYS_DEFAULT = 30;

const clampStaleDays = (value: number) =>
  Math.min(STALE_DAYS_MAX, Math.max(STALE_DAYS_MIN, value));

type EditableCompanyField = keyof Pick<
  CompanyUpdate,
  | 'corporate_status'
  | 'coverage_priority'
  | 'former_names'
  | 'acquired_by_company_id'
  | 'feed_news_rss'
  | 'press_release_url'
  | 'feed_press_release'
  | 'quartr_company_id'
>;

const DEFAULT_SERVER_NAME = 'Public';
const ALL_COMPANIES = 'all';

const isAllCompaniesScope = (id: string) => id === ALL_COMPANIES;
const isSpecificServerScope = (id: string) => id !== '' && !isAllCompaniesScope(id);

const pickDefaultServerId = (servers: SozoServerInfo[]): string => {
  const preferred = servers.find((server) => server.name === DEFAULT_SERVER_NAME);
  return preferred?.id ?? servers[0]?.id ?? '';
};

const ContentCompletionPage: React.FC = () => {
  const { user } = useAuth();
  const isEditorOrAdmin =
    user?.is_superuser || user?.role === 'admin' || user?.role === 'editor';
  const [companies, setCompanies] = useState<CompanyContentHealth[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [enhancementTargets, setEnhancementTargets] = useState<CompanyContentHealth[] | null>(null);
  const [backfillTargets, setBackfillTargets] = useState<CompanyContentHealth[] | null>(null);
  const [quartrTargets, setQuartrTargets] = useState<CompanyContentHealth[] | null>(null);
  const [urlDiscoveryTargets, setUrlDiscoveryTargets] = useState<CompanyContentHealth[] | null>(null);
  const [staleDays, setStaleDays] = useState(STALE_DAYS_DEFAULT);
  const [staleDaysDebounced, setStaleDaysDebounced] = useState(STALE_DAYS_DEFAULT);
  const [statusFilter, setStatusFilter] = useState<ContentHealthStatus | ''>('');
  const [quartrStatusFilter, setQuartrStatusFilter] = useState<QuartrHealthStatus | ''>('');
  const [corporateStatusFilter, setCorporateStatusFilter] = useState<CorporateStatusFilter | ''>('');
  const [coveragePriorityFilter, setCoveragePriorityFilter] = useState<CoveragePriorityFilter | ''>('');
  const [sortBy, setSortBy] = useState<ContentCompletionSortField>('company_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyContentHealth | null>(null);

  const [servers, setServers] = useState<SozoServerInfo[]>([]);
  const [channels, setChannels] = useState<SozoChannelInfo[]>([]);
  const [channelSummaries, setChannelSummaries] = useState<ChannelCompletionSummary[]>([]);
  const [serverId, setServerId] = useState<string>('');
  const [channelId, setChannelId] = useState<string>('');
  const [channelName, setChannelName] = useState<string | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [serversError, setServersError] = useState<string | null>(null);
  const [feedIssuesRefreshToken, setFeedIssuesRefreshToken] = useState(0);
  const [quartrIssuesRefreshToken, setQuartrIssuesRefreshToken] = useState(0);
  const [backfillActivityRefreshToken, setBackfillActivityRefreshToken] = useState(0);
  const overviewAbortRef = useRef<AbortController | null>(null);
  const hasLoadedOverviewRef = useRef(false);
  const prevServerIdRef = useRef(serverId);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => setStaleDaysDebounced(staleDays), 400);
    return () => clearTimeout(timer);
  }, [staleDays]);

  useEffect(() => {
    const loadServers = async () => {
      try {
        const data = await getSozoServers();
        setServers(data.servers);
        setServersError(null);
        setServerId((prev) => prev || pickDefaultServerId(data.servers));
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Failed to load Sozo servers';
        setServersError(msg);
      }
    };
    loadServers();
  }, []);

  useEffect(() => {
    if (!isSpecificServerScope(serverId)) {
      setChannels([]);
      if (!isAllCompaniesScope(serverId)) {
        setChannelSummaries([]);
      }
      setChannelId('');
      setChannelName(null);
      return;
    }

    setChannelSummaries([]);

    const loadChannels = async () => {
      setChannelsLoading(true);
      setChannelsError(null);
      try {
        const channelsData = await getSozoChannels(serverId);
        setChannels(channelsData.channels);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Failed to load focus-area channels';
        setChannelsError(msg);
        setChannels([]);
        setChannelSummaries([]);
      } finally {
        setChannelsLoading(false);
      }
    };

    loadChannels();
  }, [serverId]);

  const fetchOverview = useCallback(async (options?: { silent?: boolean }) => {
    if (serverId === '') {
      overviewAbortRef.current?.abort();
      setCompanies([]);
      setChannelSummaries([]);
      setLoading(false);
      setRefreshing(false);
      hasLoadedOverviewRef.current = false;
      return;
    }

    overviewAbortRef.current?.abort();
    const controller = new AbortController();
    overviewAbortRef.current = controller;

    const silent = Boolean(options?.silent);
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await getContentCompletionOverview(
        {
          stale_days: staleDaysDebounced,
          status: statusFilter || undefined,
          quartr_status: quartrStatusFilter || undefined,
          corporate_status: corporateStatusFilter || undefined,
          coverage_priority: coveragePriorityFilter || undefined,
          search: searchDebounced.trim() || undefined,
          server_id: isAllCompaniesScope(serverId) ? undefined : serverId,
          channel_id: isAllCompaniesScope(serverId) ? undefined : channelId || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) {
        return;
      }
      setCompanies(data.companies);
      setChannelSummaries(data.channel_summaries ?? []);
      setChannelName(data.channel_name ?? null);
      setSelectedCompany((prev) => {
        if (!prev) return prev;
        return data.companies.find((c) => c.company_id === prev.company_id) ?? prev;
      });
      hasLoadedOverviewRef.current = true;
    } catch (err: unknown) {
      if (
        axios.isCancel(err) ||
        (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') ||
        (err as { name?: string })?.name === 'CanceledError' ||
        (err as { name?: string })?.name === 'AbortError'
      ) {
        return;
      }
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load content completion data';
      setError(msg);
    } finally {
      if (overviewAbortRef.current === controller) {
        overviewAbortRef.current = null;
      }
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [staleDaysDebounced, statusFilter, quartrStatusFilter, corporateStatusFilter, coveragePriorityFilter, searchDebounced, serverId, channelId, sortBy, sortOrder]);

  useEffect(() => {
    if (serverId === '') {
      setCompanies([]);
      setChannelSummaries([]);
      setLoading(false);
      setRefreshing(false);
      hasLoadedOverviewRef.current = false;
      prevServerIdRef.current = serverId;
      return;
    }
    const serverChanged = prevServerIdRef.current !== serverId;
    prevServerIdRef.current = serverId;
    // Keep the table mounted while typing/filtering; full spinner only on
    // first load or server scope change.
    const silent = hasLoadedOverviewRef.current && !serverChanged;
    fetchOverview({ silent });
  }, [fetchOverview, serverId]);

  const summary = useMemo(() => {
    const counts = emptyStatusCounts();
    companies.forEach((c) => {
      counts[c.status] += 1;
    });
    return counts;
  }, [companies]);

  const quartrSummary = useMemo(() => {
    const counts = emptyQuartrStatusCounts();
    companies.forEach((c) => {
      counts[c.quartr_status] += 1;
    });
    return counts;
  }, [companies]);

  // Clear stale selections whenever the visible company set changes.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(companies.map((c) => c.company_id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [companies]);

  const toggleSelect = (companyId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === companies.length) return new Set();
      return new Set(companies.map((c) => c.company_id));
    });
  };

  const handleWebsiteSave = useCallback(
    async (companyId: string, website: string) => {
      await updateCompany(companyId, { website } as any);
      // Reflect the saved URL immediately without a full reload.
      setCompanies((prev) =>
        prev.map((c) => (c.company_id === companyId ? { ...c, website } : c))
      );
    },
    []
  );

  const handleCompanyFieldSave = useCallback(
    async (
      companyId: string,
      field: EditableCompanyField,
      value: string | string[],
      options?: { acquiredByCompanyName?: string }
    ) => {
      let payload: CompanyUpdate;
      if (field === 'former_names') {
        const names = value as string[];
        payload = { former_names: names };
      } else if (field === 'corporate_status') {
        const normalized = (value as string).trim().toLowerCase();
        payload = { corporate_status: normalized || undefined };
      } else if (field === 'coverage_priority') {
        const normalized = (value as string).trim().toLowerCase();
        payload = { coverage_priority: normalized || null };
      } else {
        const normalized = (value as string).trim();
        payload = {
          [field]: normalized === '' ? null : normalized,
        };
      }
      await updateCompany(companyId, payload);
      const patch =
        field === 'former_names'
          ? { former_names: value as string[] }
          : field === 'corporate_status'
            ? { corporate_status: (value as string).trim().toLowerCase() || undefined }
            : field === 'coverage_priority'
              ? {
                  coverage_priority:
                    (value as string).trim().toLowerCase() || undefined,
                }
            : field === 'acquired_by_company_id'
              ? {
                  acquired_by_company_id: (value as string).trim() || undefined,
                  acquired_by_company_name: (value as string).trim()
                    ? options?.acquiredByCompanyName
                    : undefined,
                }
              : { [field]: (value as string).trim() || undefined };
      setCompanies((prev) =>
        prev.map((c) => (c.company_id === companyId ? { ...c, ...patch } : c))
      );
      setSelectedCompany((prev) =>
        prev?.company_id === companyId ? { ...prev, ...patch } : prev
      );
    },
    []
  );

  const startEnhancement = () => {
    const targets = companies.filter((c) => selectedIds.has(c.company_id));
    if (targets.length === 0) return;
    setBackfillTargets(null);
    setQuartrTargets(null);
    setUrlDiscoveryTargets(null);
    setEnhancementTargets(targets);
  };

  const startBackfill = () => {
    const targets = companies.filter(
      (c) =>
        selectedIds.has(c.company_id) && !isInactiveCorporateStatus(c.corporate_status)
    );
    if (targets.length === 0) return;
    setEnhancementTargets(null);
    setQuartrTargets(null);
    setUrlDiscoveryTargets(null);
    setBackfillTargets(targets);
  };

  const startQuartrCheck = () => {
    const targets = companies.filter((c) => selectedIds.has(c.company_id));
    if (targets.length === 0) return;
    setEnhancementTargets(null);
    setBackfillTargets(null);
    setUrlDiscoveryTargets(null);
    setQuartrTargets(targets);
  };

  const startUrlDiscovery = () => {
    const targets = companies.filter((c) => selectedIds.has(c.company_id));
    if (targets.length === 0) return;
    setEnhancementTargets(null);
    setBackfillTargets(null);
    setQuartrTargets(null);
    setUrlDiscoveryTargets(targets);
  };

  const handleSort = (field: ContentCompletionSortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortOrder('asc');
  };

  const handleServerChange = (value: string) => {
    setServerId(value);
    setChannelId('');
    setChannelName(null);
  };

  const handleChannelSelect = (value: string) => {
    setChannelId(value);
    if (!value) {
      setChannelName(null);
    } else {
      const selected = channels.find((channel) => channel.id === value);
      setChannelName(selected?.name ?? null);
    }
  };

  const handleIssueReported = useCallback(() => {
    setFeedIssuesRefreshToken((prev) => prev + 1);
  }, []);

  const handleQuartrIssueReported = useCallback(() => {
    setQuartrIssuesRefreshToken((prev) => prev + 1);
  }, []);

  const handleBackfillActivityRecorded = useCallback(() => {
    setBackfillActivityRefreshToken((prev) => prev + 1);
  }, []);

  const selectedServerName = servers.find((server) => server.id === serverId)?.name;
  const scopeLabel = channelId && channelName
    ? channelName
    : isAllCompaniesScope(serverId)
      ? 'All companies'
      : selectedServerName
        ? `All focus areas on ${selectedServerName}`
        : 'Select a server';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ArticleIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Content Completion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor ingestion health by Sozo focus area and company, preview backfills, and report feed issues.
          </Typography>
        </Box>
      </Box>

      {serversError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Focus-area filters unavailable: {serversError}. Add SOZO_ADMIN_USERNAME and SOZO_ADMIN_PASSWORD to the
          backend .env (same as datatool).
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={servers.length === 0}>
            <InputLabel>Server</InputLabel>
            <Select
              value={serverId}
              label="Server"
              onChange={(e) => handleServerChange(e.target.value as string)}
            >
              <MenuItem value={ALL_COMPANIES}>All companies</MenuItem>
              {servers.map((server) => (
                <MenuItem key={server.id} value={server.id}>
                  {server.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ minWidth: 220 }}
            disabled={!isSpecificServerScope(serverId) || channelsLoading || channels.length === 0}
          >
            <InputLabel>Focus area</InputLabel>
            <Select
              value={channelId}
              label="Focus area"
              onChange={(e) => handleChannelSelect(e.target.value as string)}
            >
              <MenuItem value="">All focus areas</MenuItem>
              {channels.map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.name} ({channel.company_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Search companies"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
            disabled={serverId === ''}
            helperText={refreshing ? 'Updating results…' : undefined}
          />
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={serverId === ''}>
            <InputLabel>PR status</InputLabel>
            <Select
              value={statusFilter}
              label="PR status"
              onChange={(e) => setStatusFilter(e.target.value as ContentHealthStatus | '')}
            >
              <MenuItem value="">All</MenuItem>
              {(Object.keys(STATUS_LABELS) as ContentHealthStatus[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {STATUS_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={serverId === ''}>
            <InputLabel>Quartr status</InputLabel>
            <Select
              value={quartrStatusFilter}
              label="Quartr status"
              onChange={(e) => setQuartrStatusFilter(e.target.value as QuartrHealthStatus | '')}
            >
              <MenuItem value="">All</MenuItem>
              {(Object.keys(QUARTR_STATUS_LABELS) as QuartrHealthStatus[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {QUARTR_STATUS_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={serverId === ''}>
            <InputLabel>Corporate status</InputLabel>
            <Select
              value={corporateStatusFilter}
              label="Corporate status"
              onChange={(e) =>
                setCorporateStatusFilter(e.target.value as CorporateStatusFilter | '')
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={serverId === ''}>
            <InputLabel>Coverage priority</InputLabel>
            <Select
              value={coveragePriorityFilter}
              label="Coverage priority"
              onChange={(e) =>
                setCoveragePriorityFilter(e.target.value as CoveragePriorityFilter | '')
              }
            >
              <MenuItem value="">All</MenuItem>
              {COVERAGE_PRIORITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
              <MenuItem value="unset">Unset</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Quiet after (days)"
            type="number"
            value={staleDays}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setStaleDays(STALE_DAYS_DEFAULT);
                return;
              }
              const parsed = parseInt(raw, 10);
              if (Number.isNaN(parsed)) {
                setStaleDays(STALE_DAYS_DEFAULT);
                return;
              }
              setStaleDays(clampStaleDays(parsed));
            }}
            size="small"
            sx={{ width: 160 }}
            inputProps={{ min: STALE_DAYS_MIN, max: STALE_DAYS_MAX }}
            disabled={serverId === ''}
          />
        </Box>
      </Paper>

      {channelsError && isSpecificServerScope(serverId) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {channelsError}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        {serverId !== '' && (
          <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Viewing: {scopeLabel}
              {companies.length > 0 ? ` · ${companies.length} company(ies)` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
                PR:
              </Typography>
              {(Object.keys(STATUS_LABELS) as ContentHealthStatus[]).map((key) => (
                <Tooltip key={key} title={prStatusTooltip(key, staleDays)}>
                  <Chip
                    label={`${STATUS_LABELS[key]}: ${summary[key]}`}
                    color={STATUS_COLORS[key]}
                    variant="outlined"
                    size="small"
                  />
                </Tooltip>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
                Quartr:
              </Typography>
              {(Object.keys(QUARTR_STATUS_LABELS) as QuartrHealthStatus[]).map((key) => (
                <Tooltip key={key} title={quartrStatusTooltip(key, staleDays)}>
                  <Chip
                    label={`${QUARTR_STATUS_LABELS[key]}: ${quartrSummary[key]}`}
                    color={QUARTR_STATUS_COLORS[key]}
                    variant="outlined"
                    size="small"
                  />
                </Tooltip>
              ))}
            </Box>
          </>
        )}
      </Box>

      <CompaniesToReviewPanel
        canEdit={isEditorOrAdmin}
        refreshToken={feedIssuesRefreshToken}
        companies={companies}
        onSelectCompany={setSelectedCompany}
      />

      <FeedIssuesPanel
        canEdit={isEditorOrAdmin}
        refreshToken={feedIssuesRefreshToken}
        companies={companies}
        onSelectCompany={setSelectedCompany}
      />

      <QuartrIssuesPanel
        canEdit={isEditorOrAdmin}
        refreshToken={quartrIssuesRefreshToken}
        companies={companies}
        onSelectCompany={setSelectedCompany}
      />

      <BackfillActivityPanel
        refreshToken={backfillActivityRefreshToken}
        companies={companies}
        onSelectCompany={setSelectedCompany}
      />

      {isEditorOrAdmin && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            disabled={
              selectedIds.size === 0 ||
              Boolean(enhancementTargets) ||
              Boolean(backfillTargets) ||
              Boolean(quartrTargets) ||
              Boolean(urlDiscoveryTargets)
            }
            onClick={startEnhancement}
          >
            AI Enhancement
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PlayArrowIcon />}
            disabled={
              selectedIds.size === 0 ||
              Boolean(enhancementTargets) ||
              Boolean(backfillTargets) ||
              Boolean(quartrTargets) ||
              Boolean(urlDiscoveryTargets)
            }
            onClick={startBackfill}
          >
            Backfill
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<FactCheckIcon />}
            disabled={
              selectedIds.size === 0 ||
              Boolean(enhancementTargets) ||
              Boolean(backfillTargets) ||
              Boolean(quartrTargets) ||
              Boolean(urlDiscoveryTargets)
            }
            onClick={startQuartrCheck}
          >
            Quartr check
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<LanguageIcon />}
            disabled={
              selectedIds.size === 0 ||
              Boolean(enhancementTargets) ||
              Boolean(backfillTargets) ||
              Boolean(quartrTargets) ||
              Boolean(urlDiscoveryTargets)
            }
            onClick={startUrlDiscovery}
          >
            Discover URLs
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          {selectedIds.size > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedIds.size} selected
            </Typography>
          )}
        </Box>
      )}

      {enhancementTargets && (
        <BatchEnhancementPanel
          companies={enhancementTargets}
          onClose={() => setEnhancementTargets(null)}
          onCompanyUpdated={fetchOverview}
        />
      )}

      {backfillTargets && (
        <BatchBackfillPanel
          companies={backfillTargets}
          onClose={() => setBackfillTargets(null)}
          onComplete={() => {
            fetchOverview({ silent: true });
            handleBackfillActivityRecorded();
          }}
          onIssueReported={handleIssueReported}
        />
      )}

      {quartrTargets && (
        <BatchQuartrCheckPanel
          companies={quartrTargets}
          onClose={() => setQuartrTargets(null)}
          onComplete={() => fetchOverview({ silent: true })}
          onIssueReported={handleQuartrIssueReported}
        />
      )}

      {urlDiscoveryTargets && (
        <BatchUrlDiscoveryPanel
          companies={urlDiscoveryTargets}
          onClose={() => setUrlDiscoveryTargets(null)}
          onComplete={() => fetchOverview({ silent: true })}
          onIssueReported={handleIssueReported}
        />
      )}

      {isSpecificServerScope(serverId) && !channelId && channelSummaries.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Focus area</TableCell>
                <TableCell align="right">Companies</TableCell>
                <TableCell align="right">Recent</TableCell>
                <TableCell align="right">Quiet</TableCell>
                <TableCell align="right">No PRs yet</TableCell>
                <TableCell align="right">Needs feed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channelSummaries.map((row) => (
                <TableRow
                  key={row.channel_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  selected={false}
                  onClick={() => handleChannelSelect(row.channel_id)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.channel_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.company_count}</TableCell>
                  <TableCell align="right">{row.status_counts.active}</TableCell>
                  <TableCell align="right">{row.status_counts.stale}</TableCell>
                  <TableCell align="right">{row.status_counts.no_content}</TableCell>
                  <TableCell align="right">{row.status_counts.no_feed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {serverId === '' ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {servers.length === 0 && !serversError
              ? 'Loading servers…'
              : 'Select a server to view company content health.'}
          </Typography>
        </Paper>
      ) : loading ? (
        <LoadingSpinner message="Loading content health..." />
      ) : (
        <TableContainer component={Paper} sx={{ position: 'relative' }}>
          {refreshing && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}
            />
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                {isEditorOrAdmin && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={companies.length > 0 && selectedIds.size === companies.length}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < companies.length}
                      onChange={toggleSelectAll}
                      inputProps={{ 'aria-label': 'Select all companies' }}
                    />
                  </TableCell>
                )}
                <TableCell sortDirection={sortBy === 'company_name' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'company_name'}
                    direction={sortBy === 'company_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('company_name')}
                  >
                    Company
                  </TableSortLabel>
                </TableCell>
                <TableCell>Website</TableCell>
                <TableCell sortDirection={sortBy === 'corporate_status' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'corporate_status'}
                    direction={sortBy === 'corporate_status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('corporate_status')}
                  >
                    Corporate status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'coverage_priority' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'coverage_priority'}
                    direction={sortBy === 'coverage_priority' ? sortOrder : 'asc'}
                    onClick={() => handleSort('coverage_priority')}
                  >
                    Coverage priority
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    PR status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'last_pr_date' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'last_pr_date'}
                    direction={sortBy === 'last_pr_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('last_pr_date')}
                  >
                    Last PR
                  </TableSortLabel>
                </TableCell>
                <TableCell>PR recent</TableCell>
                <TableCell sortDirection={sortBy === 'quartr_status' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'quartr_status'}
                    direction={sortBy === 'quartr_status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('quartr_status')}
                  >
                    Quartr status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Quartr</TableCell>
                <TableCell sortDirection={sortBy === 'last_quartr_date' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'last_quartr_date'}
                    direction={sortBy === 'last_quartr_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('last_quartr_date')}
                  >
                    Last Quartr
                  </TableSortLabel>
                </TableCell>
                <TableCell>Feed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isEditorOrAdmin ? 12 : 11} align="center" sx={{ py: 4 }}>
                    No companies match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow
                    key={company.company_id}
                    hover
                    selected={selectedIds.has(company.company_id)}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCompany(company)}
                  >
                    {isEditorOrAdmin && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(company.company_id)}
                          onChange={() => toggleSelect(company.company_id)}
                          inputProps={{ 'aria-label': `Select ${company.company_name}` }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {company.company_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.company_id}
                        {company.ticker ? ` · ${company.ticker}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <WebsiteCell
                        companyId={company.company_id}
                        website={company.website}
                        canEdit={isEditorOrAdmin}
                        onSave={handleWebsiteSave}
                      />
                    </TableCell>
                    <TableCell>{formatCorporateStatus(company.corporate_status)}</TableCell>
                    <TableCell>{formatCoveragePriority(company.coverage_priority)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={prStatusTooltip(company.status, staleDays, company.last_pr_date)}
                      >
                        <Chip
                          label={STATUS_LABELS[company.status]}
                          color={STATUS_COLORS[company.status]}
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatLastContentDate(company.last_pr_date)}</TableCell>
                    <TableCell>{company.pr_recent_count}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={quartrStatusTooltip(
                          company.quartr_status,
                          staleDays,
                          company.last_quartr_date
                        )}
                      >
                        <Chip
                          label={QUARTR_STATUS_LABELS[company.quartr_status]}
                          color={QUARTR_STATUS_COLORS[company.quartr_status]}
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{company.quartr_total_count || '—'}</TableCell>
                    <TableCell>{formatLastContentDate(company.last_quartr_date)}</TableCell>
                    <TableCell>
                      {company.has_feed_configured ? (
                        <Chip label="Configured" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Missing" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CompanyDetailDrawer
        company={selectedCompany}
        staleDays={staleDays}
        canEdit={isEditorOrAdmin}
        onClose={() => setSelectedCompany(null)}
        onRefreshOverview={() => fetchOverview({ silent: true })}
        onIssueReported={handleIssueReported}
        onFieldSave={handleCompanyFieldSave}
        onBackfillComplete={handleBackfillActivityRecorded}
      />
    </Container>
  );
};

export default ContentCompletionPage;
